<template>
  <div v-if="visible" class="bill-preview section-container">
    <h2 class="section-title">账单图片生成</h2>

    <!-- 模板选择 -->
    <div class="template-selector">
      <span class="selector-label">风格：</span>
      <button
        v-for="t in templates"
        :key="t.value"
        :class="['template-btn', { active: currentTemplate === t.value }]"
        @click="currentTemplate = t.value"
      >{{ t.label }}</button>
    </div>

    <!-- 卡片选择 -->
    <div class="card-selector">
      <span class="selector-label">卡片：</span>
      <button
        :class="['card-btn', { active: currentCard === 'overview' }]"
        @click="currentCard = 'overview'"
      >总览</button>
      <button
        v-for="p in resultList"
        :key="p.name"
        :class="['card-btn', { active: currentCard === p.name }]"
        @click="currentCard = p.name"
      >{{ p.name }}</button>
    </div>

    <!-- 预览区 -->
    <div class="preview-wrapper">
      <div v-if="generating" class="generating-overlay">
        <span>正在生成图片...</span>
      </div>
      <div ref="previewArea" class="preview-area">
        <component
          :is="currentTemplateComponent"
          :result-list="currentCard === 'overview' ? resultList : [currentCardData]"
          :form="form"
          :discount-summary="discountSummary"
          :visible="true"
        />
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="bill-actions">
      <button @click="handleSave" :disabled="generating" class="btn btn-bill-save">
        {{ generating ? '生成中...' : '保存预览图' }}
      </button>
      <button @click="handleCopy" :disabled="generating" class="btn btn-bill-copy">
        复制到剪贴板
      </button>
      <button @click="handleDownloadAll" :disabled="generating" class="btn btn-bill-download">
        打包下载全部
      </button>
    </div>
  </div>
</template>

<script>
import { renderToImage, downloadBlob, downloadAsZip, copyImageToClipboard, dataUrlToBlob } from '../utils/renderer';
import TemplateMinimal from './templates/TemplateMinimal.vue';
import TemplateReceipt from './templates/TemplateReceipt.vue';
import TemplateNotebook from './templates/TemplateNotebook.vue';
import TemplatePostit from './templates/TemplatePostit.vue';

const templateMap = {
  minimal: 'TemplateMinimal',
  receipt: 'TemplateReceipt',
  notebook: 'TemplateNotebook',
  postit: 'TemplatePostit'
};

export default {
  name: 'BillPreview',
  components: {
    TemplateMinimal,
    TemplateReceipt,
    TemplateNotebook,
    TemplatePostit
  },
  props: {
    resultList: { type: Array, default: () => [] },
    form: { type: Object, default: () => ({}) },
    visible: { type: Boolean, default: false },
    discountSummary: { type: Object, default: null }
  },
  data() {
    return {
      currentTemplate: 'minimal',
      currentCard: 'overview',
      generating: false
    }
  },
  computed: {
    templates() {
      return [
        { value: 'minimal', label: '简约' },
        { value: 'receipt', label: '小票' },
        { value: 'notebook', label: '手写' },
        { value: 'postit', label: '便签' }
      ]
    },
    currentTemplateComponent() {
      return templateMap[this.currentTemplate]
    },
    currentCardData() {
      if (this.currentCard === 'overview') return null
      return this.resultList.find(r => r.name === this.currentCard) || null
    }
  },
  methods: {
    async handleSave() {
      this.generating = true
      try {
        await this.$nextTick()
        const el = this.$refs.previewArea
        const dataUrl = await renderToImage(el)
        const blob = dataUrlToBlob(dataUrl)
        const name = this.currentCard === 'overview' ? '总览' : this.currentCard
        const timestamp = new Date().toLocaleString().replace(/[/:\s]/g, '-')
        downloadBlob(blob, `AA账单_${name}_${timestamp}.png`)
      } catch (e) {
        alert('生成图片失败：' + e.message)
      }
      this.generating = false
    },

    async handleCopy() {
      this.generating = true
      try {
        await this.$nextTick()
        const el = this.$refs.previewArea
        const dataUrl = await renderToImage(el)
        await copyImageToClipboard(dataUrl)
        alert('图片已复制到剪贴板')
      } catch (e) {
        alert('复制失败（可能需要 HTTPS 环境）')
      }
      this.generating = false
    },

    async handleDownloadAll() {
      this.generating = true
      try {
        // 保存当前选择
        const savedTemplate = this.currentTemplate
        const savedCard = this.currentCard

        const items = []

        // 总览
        this.currentTemplate = savedTemplate
        this.currentCard = 'overview'
        await this.$nextTick()
        items.push({ element: this.$refs.previewArea, fileName: '0_总览.png' })

        // 各人卡片
        for (let i = 0; i < this.resultList.length; i++) {
          this.currentCard = this.resultList[i].name
          await this.$nextTick()
          items.push({
            element: this.$refs.previewArea,
            fileName: `${i + 1}_${this.resultList[i].name}.png`
          })
        }

        await downloadAsZip(items)

        // 恢复
        this.currentTemplate = savedTemplate
        this.currentCard = savedCard
      } catch (e) {
        alert('打包下载失败：' + e.message)
      }
      this.generating = false
    }
  }
}
</script>

<style scoped>
.bill-preview {
  margin-top: 15px;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

.template-selector,
.card-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
}

.template-btn,
.card-btn {
  padding: 4px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: #fafafa;
  color: #4a5568;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.template-btn.active,
.card-btn.active {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.08);
  color: #667eea;
  font-weight: 600;
}

.preview-wrapper {
  position: relative;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
  max-height: 600px;
  overflow-y: auto;
}

.preview-area {
  transform-origin: top center;
}

.generating-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: #667eea;
  z-index: 10;
  border-radius: 8px;
}

.bill-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-bill-save {
  background: #42b983;
  color: #fff;
}

.btn-bill-save:hover:not(:disabled) {
  background: #3aa876;
  transform: translateY(-1px);
}

.btn-bill-copy {
  background: #667eea;
  color: #fff;
}

.btn-bill-copy:hover:not(:disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
}

.btn-bill-download {
  background: #ed8936;
  color: #fff;
}

.btn-bill-download:hover:not(:disabled) {
  background: #dd6b20;
  transform: translateY(-1px);
}

@media (max-width: 750px) {
  .preview-wrapper {
    max-height: 400px;
    padding: 8px;
  }

  .section-title {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .btn {
    font-size: 13px;
    padding: 8px 12px;
  }
}
</style>
